import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Loader2, ArrowRight, ArrowLeft, ShieldCheck, Smartphone, CheckCircle2,
  Download, Copy, XCircle, Share2, Printer, Mail, MessageCircle,
  Home, RefreshCcw, LifeBuoy, Lock, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { api, API } from "../lib/api";

/* ═══════════════════════════════════════════════════════════════════
   SM888 — Mobile-only secure payment link
   /SM888                → amount → customer info → summary → gateway
   /SM888/status/:id     → confirmation / failure / pending
   Theme: orange · green · white (light, standalone branding)
   ═══════════════════════════════════════════════════════════════════ */

const useIsMobile = () => {
  const [mobile, setMobile] = useState(true);
  useEffect(() => {
    const check = () => {
      const w = typeof window !== "undefined" ? window.innerWidth : 0;
      const ua = (typeof navigator !== "undefined" ? navigator.userAgent : "").toLowerCase();
      setMobile(/android|iphone|ipod|windows phone|iemobile|opera mini|mobile/i.test(ua) || w <= 900);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return mobile;
};

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtIST = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) + " IST";
  } catch { return iso || "—"; }
};

function Header() {
  return (
    <div className="flex items-center justify-between pb-4 mb-5 border-b border-orange-100">
      <div className="flex items-center gap-3">
        <img src="/optivize-logo-mark.png" alt="Optivize" className="h-10 w-auto" onError={(e)=>{e.currentTarget.style.display='none';}}/>
        <div className="min-w-0">
          <div className="text-base font-extrabold text-slate-900 leading-none" style={{fontFamily:"Outfit"}}>Optivize</div>
          <div className="text-[10px] font-semibold tracking-widest text-slate-500 uppercase mt-1">
            A Unit of <span className="text-orange-600">SURAJIT MAJHI HUF</span>
          </div>
        </div>
      </div>
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold uppercase tracking-wider" data-testid="sm888-secure-badge">
        <Lock size={11}/> Secure Payment
      </div>
    </div>
  );
}

function PageFooter({ business }) {
  return (
    <div className="text-center mt-6 space-y-1.5">
      <div className="flex items-center gap-2 text-[11px] text-slate-500 justify-center">
        <ShieldCheck size={12} className="text-emerald-600"/>
        <span>256-bit encrypted · PCI-DSS compliant gateways</span>
      </div>
      <p className="text-[10px] text-slate-400">
        © {new Date().getFullYear()} Optivize — A Unit of SURAJIT MAJHI HUF · {business?.email || "sales@optivize.digital"} · {business?.phone || ""}
      </p>
    </div>
  );
}

function Shell({ children, business }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-emerald-50/60 px-4 py-6 flex flex-col" data-testid="sm888-page">
      <div className="w-full max-w-md mx-auto flex-1">
        <div className="bg-white rounded-2xl shadow-xl ring-1 ring-orange-100 p-5 sm:p-6">
          <Header/>
          {children}
        </div>
        <PageFooter business={business}/>
      </div>
    </div>
  );
}

function DesktopQrHint() {
  const currentUrl = typeof window !== "undefined"
    ? `${window.location.origin}/SM888` : "https://optivize.digital/SM888";
  return (
    <div className="mb-5 rounded-xl bg-gradient-to-r from-orange-50 to-emerald-50 border border-orange-100 p-3 flex items-center gap-3" data-testid="sm888-desktop-qr-hint">
      <div className="shrink-0 bg-white rounded-lg p-1.5 border border-orange-100">
        <QRCodeSVG value={currentUrl} size={72} level="M" bgColor="#ffffff" fgColor="#0f172a"/>
      </div>
      <div className="min-w-0">
        <div className="text-xs font-bold text-slate-800 inline-flex items-center gap-1.5"><Smartphone size={13} className="text-orange-500"/> Prefer paying on your phone?</div>
        <p className="text-[11px] text-slate-500 mt-0.5">Scan this QR with your phone camera to open the same secure payment page — or simply continue below.</p>
      </div>
    </div>
  );
}

const Field = ({ label, error, children }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">{label}</label>
    {children}
    {error && <p className="text-[11px] text-rose-600 mt-1" data-testid={`sm888-err-${label.toLowerCase().replace(/[^a-z]+/g,"-")}`}>{error}</p>}
  </div>
);

const inputCls = "w-full px-4 py-3.5 rounded-xl bg-white border-2 border-slate-200 focus:border-orange-400 focus:outline-none text-slate-900 text-base transition-colors";

const loadRazorpay = () => new Promise((resolve, reject) => {
  if (window.Razorpay) return resolve();
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = resolve; s.onerror = () => reject(new Error("Could not load Razorpay"));
  document.body.appendChild(s);
});

export function SM888Pay() {
  const mobile = useIsMobile();
  const nav = useNavigate();
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState("");
  const [amountErr, setAmountErr] = useState("");
  const [form, setForm] = useState({ first_name:"", last_name:"", phone:"", email:"", address:"", city:"", state:"", postal_code:"" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/sm888/config").then(r => setConfig(r.data)).catch(() => setConfig({ enabled:false }));
  }, []);

  const amt = parseFloat(amount);
  const plan = useMemo(() => {
    if (!config || !amount || isNaN(amt) || amt <= 0) return null;
    if (amt >= (config.enterprise_threshold || 100000)) return { enterprise: true };
    return config.plans?.find(p => amt >= p.min && amt <= p.max) || null;
  }, [config, amount, amt]);

  if (!config) return (
    <div className="min-h-screen bg-orange-50 flex items-center justify-center"><Loader2 className="animate-spin text-orange-500" size={32}/></div>
  );
  if (!config.enabled) return (
    <Shell business={config.business}>
      <div className="text-center py-8" data-testid="sm888-disabled">
        <XCircle size={40} className="mx-auto text-slate-400"/>
        <h2 className="text-lg font-bold text-slate-900 mt-3">This payment link is currently unavailable</h2>
        <p className="text-sm text-slate-500 mt-1.5">Please contact Optivize support for assistance.</p>
      </div>
    </Shell>
  );

  const validateAmount = () => {
    if (!/^\d+(\.\d{1,2})?$/.test(String(amount).trim())) { setAmountErr("Amount must contain numbers only."); return false; }
    if (!(amt > 0)) { setAmountErr("Amount must be greater than zero."); return false; }
    if (amt >= (config.enterprise_threshold || 100000)) { setAmountErr(""); return false; }
    if (amt < config.min_amount || amt > config.max_amount) {
      setAmountErr(`Amount must be between ₹${config.min_amount.toLocaleString("en-IN")} and ₹${config.max_amount.toLocaleString("en-IN")}.`); return false;
    }
    if (!plan) { setAmountErr("No plan is available for this amount."); return false; }
    setAmountErr(""); return true;
  };

  const validateInfo = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "First name is required.";
    if (!form.last_name.trim()) e.last_name = "Last name is required.";
    const ph = form.phone.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, "");
    if (!/^[6-9]\d{9}$/.test(ph)) e.phone = "Enter a valid 10-digit Indian mobile number.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) e.email = "Enter a valid email address.";
    if (form.address.trim().length < 3) e.address = "Address is required.";
    if (form.city.trim().length < 2) e.city = "City is required.";
    if (form.state.trim().length < 2) e.state = "State is required.";
    if (!/^[1-9]\d{5}$/.test(form.postal_code.trim())) e.postal_code = "Enter a valid 6-digit PIN code.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const proceedToPay = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const ph = form.phone.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, "");
      const { data: order } = await api.post("/sm888/orders", { ...form, phone: ph, amount: amt });
      const { data: sess } = await api.post(`/sm888/orders/${order.id}/pay`);
      await launchGateway(order.id, sess, nav);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start the payment. Please try again.");
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <Shell business={config.business}>
      {config.mobile_only && !mobile && <DesktopQrHint/>}
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-5">
        {[1,2,3].map(i => (
          <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-gradient-to-r from-orange-400 to-emerald-500" : "bg-slate-100"}`}/>
        ))}
      </div>

      {step === 1 && (
        <div data-testid="sm888-step-amount">
          <h1 className="text-2xl font-extrabold text-slate-900" style={{fontFamily:"Outfit"}}>Make a payment</h1>
          <p className="text-sm text-slate-500 mt-1">Enter the amount — your plan is selected automatically.</p>
          <div className="mt-5 space-y-4">
            <Field label="Amount (INR)" error={amountErr}>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold text-lg">₹</span>
                <input inputMode="decimal" value={amount}
                  onChange={(e)=>{ setAmount(e.target.value.replace(/[^\d.]/g,"")); setAmountErr(""); }}
                  placeholder="0.00" className={`${inputCls} pl-9 text-xl font-bold`} data-testid="sm888-amount-input"/>
              </div>
            </Field>
            <Field label="Selected Plan">
              <input readOnly value={plan?.enterprise ? "Custom Enterprise Plan" : (plan?.name || "—")}
                className={`${inputCls} bg-slate-50 font-semibold ${plan && !plan.enterprise ? "text-emerald-700 border-emerald-200" : "text-slate-500"}`}
                data-testid="sm888-plan-field"/>
            </Field>
            {plan?.enterprise && (
              <div className="rounded-xl bg-orange-50 border border-orange-200 p-3.5 text-sm text-orange-800" data-testid="sm888-enterprise-msg">
                For payments of ₹1,00,000 or more, please contact Optivize for a customised Enterprise Plan and payment link.
                <a href={`mailto:${config.business?.email || "sales@optivize.digital"}`} className="block mt-2 font-bold text-orange-700 underline">Contact Optivize →</a>
              </div>
            )}
            <button onClick={()=>{ if (validateAmount()) setStep(2); }}
              disabled={!!plan?.enterprise}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:opacity-40 text-white font-bold text-base inline-flex items-center justify-center gap-2 shadow-lg shadow-orange-200 transition-all active:scale-[0.98]"
              data-testid="sm888-continue-btn">
              Continue <ArrowRight size={18}/>
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div data-testid="sm888-step-info">
          <button onClick={()=>setStep(1)} className="text-xs text-slate-500 inline-flex items-center gap-1 mb-3" data-testid="sm888-back-to-amount"><ArrowLeft size={12}/> Back</button>
          <h1 className="text-xl font-extrabold text-slate-900" style={{fontFamily:"Outfit"}}>Your details</h1>
          <p className="text-sm text-slate-500 mt-1">Paying <b className="text-emerald-700">{inr(amt)}</b> · {plan?.name}</p>
          <div className="mt-4 space-y-3.5">
            <div className="grid grid-cols-2 gap-3">
              <Field label="First Name" error={errors.first_name}>
                <input value={form.first_name} onChange={set("first_name")} className={inputCls} data-testid="sm888-first-name"/>
              </Field>
              <Field label="Last Name" error={errors.last_name}>
                <input value={form.last_name} onChange={set("last_name")} className={inputCls} data-testid="sm888-last-name"/>
              </Field>
            </div>
            <Field label="Phone Number" error={errors.phone}>
              <input inputMode="tel" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" className={inputCls} data-testid="sm888-phone"/>
            </Field>
            <Field label="Email Address" error={errors.email}>
              <input inputMode="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="sm888-email"/>
            </Field>
            <Field label="Address" error={errors.address}>
              <input value={form.address} onChange={set("address")} className={inputCls} data-testid="sm888-address"/>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="City" error={errors.city}>
                <input value={form.city} onChange={set("city")} className={inputCls} data-testid="sm888-city"/>
              </Field>
              <Field label="State" error={errors.state}>
                <input value={form.state} onChange={set("state")} className={inputCls} data-testid="sm888-state"/>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Country">
                <input readOnly value="India" className={`${inputCls} bg-slate-50 text-slate-500`} data-testid="sm888-country"/>
              </Field>
              <Field label="ZIP / Postal Code" error={errors.postal_code}>
                <input inputMode="numeric" maxLength={6} value={form.postal_code} onChange={set("postal_code")} className={inputCls} data-testid="sm888-postal"/>
              </Field>
            </div>
            <button onClick={()=>{ if (validateInfo()) setStep(3); }}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold text-base inline-flex items-center justify-center gap-2 shadow-lg shadow-emerald-200 transition-all active:scale-[0.98]"
              data-testid="sm888-review-btn">
              Review order <ArrowRight size={18}/>
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div data-testid="sm888-step-summary">
          <button onClick={()=>setStep(2)} className="text-xs text-slate-500 inline-flex items-center gap-1 mb-3" data-testid="sm888-back-to-info"><ArrowLeft size={12}/> Back</button>
          <h1 className="text-xl font-extrabold text-slate-900" style={{fontFamily:"Outfit"}}>Order summary</h1>
          <div className="mt-4 rounded-xl border border-slate-200 divide-y divide-slate-100 text-sm" data-testid="sm888-summary-card">
            {[
              ["Customer", `${form.first_name} ${form.last_name}`],
              ["Mobile", form.phone],
              ["Email", form.email],
              ["Selected Plan", plan?.name],
              ["Payment Amount", inr(amt)],
              ["Payment Gateway", "Secure Optivize gateway"],
            ].map(([k,v]) => (
              <div key={k} className="flex justify-between gap-3 px-4 py-2.5">
                <span className="text-slate-500">{k}</span>
                <span className="font-semibold text-slate-900 text-right break-all">{v}</span>
              </div>
            ))}
            <div className="flex justify-between gap-3 px-4 py-3 bg-emerald-50">
              <span className="font-bold text-emerald-800">Total Payable</span>
              <span className="font-extrabold text-emerald-700 text-lg" data-testid="sm888-total-payable">{inr(amt)}</span>
            </div>
          </div>
          <button onClick={proceedToPay} disabled={busy}
            className="w-full mt-5 py-4 rounded-xl bg-gradient-to-r from-orange-500 via-orange-600 to-emerald-600 hover:brightness-110 disabled:opacity-60 text-white font-bold text-base inline-flex items-center justify-center gap-2 shadow-lg shadow-orange-200 transition-all active:scale-[0.98]"
            data-testid="sm888-pay-btn">
            {busy ? <><Loader2 size={18} className="animate-spin"/> Opening secure checkout…</> : <><Lock size={16}/> Proceed to Secure Payment</>}
          </button>
        </div>
      )}
    </Shell>
  );
}

async function launchGateway(orderId, sess, nav) {
  if (sess.gateway === "payglocal") {
    window.location.href = sess.redirect_url;
    return;
  }
  await loadRazorpay();
  const rzp = new window.Razorpay({
    key: sess.razorpay_key_id,
    order_id: sess.razorpay_order_id,
    amount: sess.amount,
    currency: sess.currency,
    name: "Optivize",
    description: "Optivize service plan payment",
    image: "/optivize-logo-mark.png",
    prefill: { name: sess.customer?.name, email: sess.customer?.email, contact: sess.customer?.phone },
    theme: { color: "#ea580c" },
    handler: async (resp) => {
      try {
        await api.post(`/sm888/orders/${orderId}/razorpay/confirm`, {
          razorpay_payment_id: resp.razorpay_payment_id,
          razorpay_order_id: resp.razorpay_order_id,
          razorpay_signature: resp.razorpay_signature,
        });
      } catch { /* status page reconciles via webhook */ }
      nav(`/SM888/status/${orderId}`, { replace: true });
    },
    modal: { ondismiss: () => nav(`/SM888/status/${orderId}`) },
  });
  rzp.on("payment.failed", () => nav(`/SM888/status/${orderId}`));
  rzp.open();
}

/* ─────────────────────────── Status / Confirmation ─────────────────────── */

export function SM888Status() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const [config, setConfig] = useState(null);
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const polls = useRef(0);
  const downloaded = useRef(false);

  useEffect(() => { api.get("/sm888/config").then(r=>setConfig(r.data)).catch(()=>setConfig({})); }, []);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/sm888/orders/${orderId}`);
        if (!alive) return;
        setOrder(data);
        if (["pending_payment","payment_processing","webhook_verification_pending"].includes(data.status) && polls.current < 12) {
          polls.current += 1;
          setTimeout(poll, 3000);
        }
      } catch { if (alive) setNotFound(true); }
    };
    poll();
    return () => { alive = false; };
  }, [orderId]);

  const receiptUrl = `${API}/sm888/orders/${orderId}/receipt.pdf`;
  const invoiceUrl = `${API}/sm888/orders/${orderId}/invoice.pdf`;

  // Auto-download receipt exactly once per order (survives refresh via localStorage)
  useEffect(() => {
    if (!order || !config) return;
    if (order.status !== "successful" || !config.auto_download || downloaded.current) return;
    const key = `sm888_dl_${orderId}`;
    if (localStorage.getItem(key)) return;
    downloaded.current = true;
    localStorage.setItem(key, "1");
    const a = document.createElement("a");
    a.href = receiptUrl; a.download = `${order.receipt_number || "receipt"}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success("Receipt download started");
  }, [order, config, orderId, receiptUrl]);

  const summaryText = order ? (
    `Optivize Payment Receipt\n` +
    `Customer: ${order.customer?.name}\nPlan: ${order.plan_name}\nAmount: ${inr(order.total_amount)}\n` +
    `Order ID: ${order.order_number}\nTransaction: ${order.gateway_txn_id || "—"}\n` +
    `Receipt: ${order.receipt_number}\nInvoice: ${order.invoice_number}\n` +
    `Receipt PDF: ${receiptUrl}`
  ) : "";

  const share = async (doc) => {
    const url = doc === "invoice" ? invoiceUrl : receiptUrl;
    const title = doc === "invoice" ? "Optivize Tax Invoice" : "Optivize Payment Receipt";
    if (navigator.share) {
      try { await navigator.share({ title, text: summaryText, url }); return; } catch { /* fallback below */ }
    }
    toast.info("Use the WhatsApp / Email / Copy options below");
  };

  const retry = async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      const { data: sess } = await api.post(`/sm888/orders/${orderId}/pay`);
      await launchGateway(orderId, sess, nav);
      polls.current = 0;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not restart the payment.");
    } finally { setRetrying(false); }
  };

  if (notFound) return (
    <Shell business={config?.business}>
      <div className="text-center py-8"><XCircle size={40} className="mx-auto text-slate-400"/>
        <h2 className="text-lg font-bold text-slate-900 mt-3">Payment not found</h2></div>
    </Shell>
  );
  if (!order) return (
    <div className="min-h-screen bg-orange-50 flex items-center justify-center"><Loader2 className="animate-spin text-orange-500" size={32}/></div>
  );

  const processing = ["pending_payment","payment_processing","webhook_verification_pending","created"].includes(order.status);
  const success = ["successful","refunded","partially_refunded"].includes(order.status);
  const wa = config?.business?.whatsapp?.replace(/\D/g, "") || "";

  return (
    <Shell business={config?.business}>
      {processing && (
        <div className="text-center py-6" data-testid="sm888-processing">
          <Loader2 size={42} className="mx-auto text-orange-500 animate-spin"/>
          <h1 className="text-xl font-extrabold text-slate-900 mt-4" style={{fontFamily:"Outfit"}}>Confirming your payment…</h1>
          <p className="text-sm text-slate-500 mt-2">We're verifying with the payment gateway. This usually takes a few seconds.</p>
          <div className="mt-4 text-xs text-slate-400">Order {order.order_number}</div>
          <button onClick={retry} disabled={retrying}
            className="mt-6 w-full py-3.5 rounded-xl border-2 border-orange-300 text-orange-700 font-bold inline-flex items-center justify-center gap-2"
            data-testid="sm888-retry-processing">
            {retrying ? <Loader2 size={16} className="animate-spin"/> : <RefreshCcw size={16}/>} Retry Payment
          </button>
        </div>
      )}

      {success && (
        <div data-testid="sm888-success">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle2 size={36} className="text-emerald-600"/>
            </div>
            <h1 className="text-2xl font-extrabold text-emerald-700 mt-3" style={{fontFamily:"Outfit"}}>Payment Successful</h1>
            <p className="text-sm text-slate-500 mt-1">Thank you, {order.customer?.first_name}! A confirmation email with your receipt &amp; invoice is on its way.</p>
          </div>
          <div className="mt-5 rounded-xl border border-emerald-100 divide-y divide-slate-100 text-sm" data-testid="sm888-success-details">
            {[
              ["Customer", order.customer?.name],
              ["Selected Plan", order.plan_name],
              ["Amount Paid", inr(order.total_amount)],
              ["Date & Time", fmtIST(order.paid_at)],
              ["Order ID", order.order_number],
              ["Payment Gateway", order.gateway === "payglocal" ? "PayGlocal" : "Razorpay"],
              ["Transaction ID", order.gateway_txn_id],
              ["Status", order.status.replace(/_/g, " ").toUpperCase()],
              ["Invoice Number", order.invoice_number],
              ["Receipt Number", order.receipt_number],
            ].map(([k,v]) => (
              <div key={k} className="flex justify-between gap-3 px-4 py-2.5">
                <span className="text-slate-500">{k}</span>
                <span className="font-semibold text-slate-900 text-right break-all">{v || "—"}</span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2.5 mt-5">
            <a href={receiptUrl} download className="py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold inline-flex items-center justify-center gap-1.5" data-testid="sm888-dl-receipt"><Download size={15}/> Receipt</a>
            <a href={invoiceUrl} download className="py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold inline-flex items-center justify-center gap-1.5" data-testid="sm888-dl-invoice"><FileText size={15}/> Tax Invoice</a>
            {config?.share_enabled && (<>
              <button onClick={()=>share("receipt")} className="py-3 rounded-xl border-2 border-emerald-200 text-emerald-700 text-sm font-bold inline-flex items-center justify-center gap-1.5" data-testid="sm888-share-receipt"><Share2 size={15}/> Share Receipt</button>
              <button onClick={()=>share("invoice")} className="py-3 rounded-xl border-2 border-orange-200 text-orange-700 text-sm font-bold inline-flex items-center justify-center gap-1.5" data-testid="sm888-share-invoice"><Share2 size={15}/> Share Invoice</button>
            </>)}
            <a href={receiptUrl} target="_blank" rel="noreferrer" className="py-3 rounded-xl border-2 border-slate-200 text-slate-700 text-sm font-bold inline-flex items-center justify-center gap-1.5" data-testid="sm888-print-receipt"><Printer size={15}/> Print Receipt</a>
            <a href="https://optivize.digital" className="py-3 rounded-xl border-2 border-slate-200 text-slate-700 text-sm font-bold inline-flex items-center justify-center gap-1.5" data-testid="sm888-return-home"><Home size={15}/> Optivize.digital</a>
          </div>

          {config?.share_enabled && (
            <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">More ways to share</div>
              <div className="flex flex-wrap gap-2">
                <a href={`https://wa.me/?text=${encodeURIComponent(summaryText)}`} target="_blank" rel="noreferrer"
                  className="px-3 py-2 rounded-lg bg-emerald-100 text-emerald-800 text-xs font-bold inline-flex items-center gap-1.5" data-testid="sm888-share-wa"><MessageCircle size={13}/> WhatsApp</a>
                <a href={`mailto:?subject=${encodeURIComponent("Optivize Payment Receipt")}&body=${encodeURIComponent(summaryText)}`}
                  className="px-3 py-2 rounded-lg bg-orange-100 text-orange-800 text-xs font-bold inline-flex items-center gap-1.5" data-testid="sm888-share-email"><Mail size={13}/> Email</a>
                <button onClick={async()=>{ try{ await navigator.clipboard.writeText(summaryText); toast.success("Payment summary copied"); }catch{ toast.error("Could not copy"); } }}
                  className="px-3 py-2 rounded-lg bg-slate-200 text-slate-800 text-xs font-bold inline-flex items-center gap-1.5" data-testid="sm888-copy-summary"><Copy size={13}/> Copy summary</button>
              </div>
            </div>
          )}
        </div>
      )}

      {!processing && !success && (
        <div data-testid="sm888-failed">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center mx-auto">
              <XCircle size={36} className="text-rose-600"/>
            </div>
            <h1 className="text-2xl font-extrabold text-rose-700 mt-3" style={{fontFamily:"Outfit"}}>
              Payment {order.status === "cancelled" ? "Cancelled" : order.status === "expired" ? "Expired" : "Failed"}
            </h1>
            {order.failure_reason && <p className="text-sm text-slate-500 mt-1.5" data-testid="sm888-failure-reason">{order.failure_reason}</p>}
          </div>
          <div className="mt-5 rounded-xl border border-slate-200 divide-y divide-slate-100 text-sm">
            {[["Order ID", order.order_number], ["Amount", inr(order.total_amount)], ["Plan", order.plan_name],
              ["Status", order.status.replace(/_/g, " ").toUpperCase()]].map(([k,v]) => (
              <div key={k} className="flex justify-between gap-3 px-4 py-2.5">
                <span className="text-slate-500">{k}</span><span className="font-semibold text-slate-900">{v}</span>
              </div>
            ))}
          </div>
          <div className="mt-5 space-y-2.5">
            <button onClick={retry} disabled={retrying}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold inline-flex items-center justify-center gap-2 shadow-lg shadow-orange-200"
              data-testid="sm888-retry-btn">
              {retrying ? <Loader2 size={16} className="animate-spin"/> : <RefreshCcw size={16}/>} Retry Payment
            </button>
            <a href="https://optivize.digital" className="block w-full py-3.5 rounded-xl border-2 border-slate-200 text-slate-700 font-bold text-center" data-testid="sm888-failed-home">Return to Website</a>
            <div className="flex gap-2.5">
              {wa && <a href={`https://wa.me/${wa}?text=${encodeURIComponent(`Hi, I need help with SM888 payment ${order.order_number}`)}`} target="_blank" rel="noreferrer"
                className="flex-1 py-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-bold inline-flex items-center justify-center gap-1.5" data-testid="sm888-support-wa"><MessageCircle size={14}/> WhatsApp Support</a>}
              <a href={`mailto:${config?.business?.email || "sales@optivize.digital"}?subject=${encodeURIComponent(`Payment help · ${order.order_number}`)}`}
                className="flex-1 py-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-700 text-sm font-bold inline-flex items-center justify-center gap-1.5" data-testid="sm888-support-email"><LifeBuoy size={14}/> Contact Support</a>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}

export default SM888Pay;
